import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';

function VariableTable({ taxYear = "2024/2025", isScottish = false, compact = false }) {
  // Format the constant value for display
  const formatValue = (value) => {
    if (typeof value === 'number') {
      // If it's a percentage (less than 1)
      if (value < 1) {
        return `${(value * 100).toFixed(1)}%`;
      }
      // If it's a whole number
      return value.toLocaleString();
    }
    return value;
  };

  // Format the constant name for display
  const formatConstantName = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .replace(/Cgt/g, 'CGT')
      .replace(/Nic/g, 'NIC')
      .replace(/Rsu/g, 'RSU');
  };

  const columns = compact
    ? [
        {
          field: 'variable',
          headerName: 'Variable Name',
          flex: 1,
          sortable: true,
        },
        {
          field: 'amount',
          headerName: 'Amount',
          flex: 0.6,
          align: 'right',
          sortable: true,
        },
      ]
    : [
        {
          field: 'variable',
          headerName: 'Variable Name',
          width: 400,
          sortable: true,
        },
        {
          field: 'amount',
          headerName: 'Amount',
          width: 150,
          align: 'right',
          sortable: true,
        },
      ];

  // Create rows from the tax constants
  const createRows = () => {
    const commonVariables = [
      { id: 1, variable: 'Max Annual Pension Allowance', amount: "60,000" },
      { id: 2, variable: 'Basic Rate Tax Limit', amount: "37,700" },
      { id: 3, variable: 'Basic Rate Tax', amount: "20.0%" },
      { id: 4, variable: 'Higher Rate Tax Limit', amount: "125,140" },
      { id: 5, variable: 'Higher Rate Tax', amount: "40.0%" },
      { id: 6, variable: 'Additional Rate Tax', amount: "45.0%" },
      { id: 7, variable: 'Annual Personal Allowance Threshold', amount: "100,000" },
      { id: 8, variable: 'Annual Personal Allowance', amount: "12,570" },
      { id: 9, variable: 'National Insurance Contributions - Primary Threshold', amount: "12,584" },
      { id: 10, variable: 'National Insurance Contributions - Upper Earnings Limit', amount: "50,284" },
      { id: 11, variable: 'National Insurance Contributions - Upper Rate', amount: "2.0%" },
      { id: 12, variable: 'National Insurance Contributions - Lower Rate', amount: "8.0%" },
      { id: 13, variable: 'Capital Gains Tax - Exempt Amount', amount: "3,000" },
      { id: 14, variable: 'Capital Gains Tax - Basic Rate Limit', amount: "50,270" },
      { id: 15, variable: 'Capital Gains Tax - Lower Rate', amount: "10.0%" },
      { id: 16, variable: 'Capital Gains Tax - Higher Rate', amount: "20.0%" },
      { id: 17, variable: 'RSU Employer NIC Rate', amount: "6.9%" }
    ];

    const scottishVariables = [
      { id: 18, variable: 'Scottish Starter Rate', amount: "19.0%" },
      { id: 19, variable: 'Scottish Basic Rate', amount: "20.0%" },
      { id: 20, variable: 'Scottish Intermediate Rate', amount: "21.0%" },
      { id: 21, variable: 'Scottish Higher Rate', amount: "42.0%" },
      { id: 22, variable: 'Scottish Top Rate', amount: "48.0%" },
      { id: 23, variable: 'Scottish Starter Rate Limit', amount: "14,876" },
      { id: 24, variable: 'Scottish Basic Rate Limit', amount: "26,561" },
      { id: 25, variable: 'Scottish Intermediate Rate Limit', amount: "43,662" },
      { id: 26, variable: 'Scottish Higher Rate Limit', amount: "75,000" }
    ];

      // Add student loan variables
    const studentLoanVariables = [
      { id: 30, variable: 'Student Loan Plan 1 Threshold', amount: "22,095" },
      { id: 31, variable: 'Student Loan Plan 2 Threshold', amount: "27,295" },
      { id: 32, variable: 'Student Loan Plan 4 Threshold (Scotland)', amount: "31,395" },
      { id: 33, variable: 'Student Loan Plan 5 Threshold', amount: "25,000" },
      { id: 34, variable: 'Postgraduate Loan Threshold', amount: "21,000" },
      { id: 35, variable: 'Student Loan Plan 1-5 Rate', amount: "9.0%" },
      { id: 36, variable: 'Postgraduate Loan Rate', amount: "6.0%" },
    ];

    if (isScottish) {
      return [...commonVariables, ...scottishVariables, ...studentLoanVariables];
    } else {
      return [...commonVariables, ...studentLoanVariables];
    }
  };

  return (
    <Box sx={{ height: 'auto', width: '100%', marginTop: 2 }}>
      <DataGrid
        rows={createRows()}
        columns={columns}
        pageSize={15}
        rowsPerPageOptions={[15, 20, 25]}
        autoHeight
        disableColumnMenu
        disableSelectionOnClick
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': {
            padding: compact ? '6px' : '8px',
            fontSize: compact ? '12px' : '14px',
          },
          '& .MuiDataGrid-row': {
            '&:nth-of-type(odd)': {
              backgroundColor: '#f5f5f5',
            },
          },
          '& .MuiDataGrid-columnHeaders': {
            fontSize: compact ? '12px' : '14px',
          },
        }}
      />
    </Box>
  );
}

export default VariableTable;